/** Ventora fan (S4PB-0916176901) scene UI ids and cloud prop mapping helpers. */

export const VENTORA_FAN_SCENE_UI = {
	MODE: 'fan_mode',
	ON: 'fan_on',
	SPEED: 'fan_speed',
	SOURCE: 'fan_source',
} as const;

/** Fan run modes written to scene `setup` as `mode`. */
export const VENTORA_FAN_RUN_MODES = ['auto', 'manual'] as const;

export type VentoraFanRunMode = (typeof VENTORA_FAN_RUN_MODES)[number];

/** Cloud keys for Ventora fan Do setup and When conditions (no channel suffix). */
export const VENTORA_FAN_CLOUD_KEYS = {
	MODE: 'mode',
	ON: 'on',
	SPEED: 'speed',
	SOURCE: 'source',
} as const;

/** Auto-mode active source values exposed as `source` in the scene service. */
export const VENTORA_FAN_SOURCE_VALUES = [
	'idle_refresh',
	'light',
	'temp',
	'hum',
	'boost',
	'night_mode',
	'night_mode_end_period',
	'force',
] as const;

export type VentoraFanSourceValue = (typeof VENTORA_FAN_SOURCE_VALUES)[number];

/** i18n keys for Ventora fan auto-mode source labels in scenes. */
export const VENTORA_FAN_SOURCE_I18N: Record<VentoraFanSourceValue, string> = {
	idle_refresh: 'sub_settings_title_ventora_idle_refresh',
	light: 'sub_settings_title_ventora_light_trigger',
	temp: 'sub_settings_title_ventora_temp_trigger',
	hum: 'sub_settings_title_ventora_hum_trigger',
	boost: 'ventora_status_boost',
	night_mode: 'sub_settings_title_ventora_night_mode',
	night_mode_end_period: 'ventora_source_night_mode_end_period',
	force: 'ventora_in_mode_force',
};

/** Icons for Ventora fan auto-mode source values in scene UI. */
export const VENTORA_FAN_SOURCE_ICONS: Record<VentoraFanSourceValue, string> = {
	idle_refresh: 'fas fa-fan',
	light: 'fas fa-sun',
	temp: 'fas fa-thermometer-half',
	hum: 'fas fa-droplet',
	boost: 'fas fa-bolt',
	night_mode: 'fas fa-moon',
	night_mode_end_period: 'fas fa-moon',
	force: 'fas fa-gauge-high',
};

export const ventoraFanOnUiToCloud = (value: string | boolean): boolean => {
	if (typeof value === 'boolean') return value;
	return value === 'on';
};

export const ventoraFanOnCloudToUi = (value: unknown): 'on' | 'off' => {
	if (value === true || value === 'true') return 'on';
	return 'off';
};

/** Resolves the i18n key for a Ventora fan auto-mode source value, if known. */
export const getVentoraFanSourceI18nKey = (source: unknown): string | undefined => {
	const normalized = String(source ?? '').trim();
	if (!normalized) return undefined;
	return VENTORA_FAN_SOURCE_I18N[normalized as VentoraFanSourceValue];
};

/** Resolves the icon for a Ventora fan auto-mode source value, if known. */
export const getVentoraFanSourceIcon = (source: unknown): string => {
	const normalized = String(source ?? '').trim();
	if (!normalized) return 'fas fa-fan';
	return VENTORA_FAN_SOURCE_ICONS[normalized as VentoraFanSourceValue] ?? 'fas fa-fan';
};
